//Built-in libraries

//Third party libraries
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useParams } from 'react-router';
import { signApplicationPublicAsset } from '@/api/Rover/ApplicationForm';
//Custom components
import Button from '@/components/UI/Button';
import { HookFileInput } from '@/components/UI/HookForm/Form/File';
import { HookInput } from '@/components/UI/HookForm/Form/Input';
import HookMaskedInput from '@/components/UI/HookForm/Form/MaskedInput';
import { HookRadioGroup } from '@/components/UI/HookForm/Form/RadioGroup';
import { HookSelect } from '@/components/UI/HookForm/Form/Select';
import { HookTextarea } from '@/components/UI/HookForm/Form/TextArea';
import { useApplicationFormContext } from '@/features/ApplicationForm/ApplicationFormProvider';
//Services
import { type UploadFnReturn, useFileUpload } from '@/hooks/useFileUpload';
import { compareTwoArrays } from '@/utils';

//STP Libraries

import type { InferType } from 'yup';
import type { ISignApplicationPublicAssetPayload } from '@/api/Rover/ApplicationForm/types';
import type { SignedApplicationAssetResponse } from '@/types/Shared';
//Assets and Miscellaneous
import { States } from '../../../../../../constants';
import type { RentalRegistryValidationSchema } from '../validationSchema';
import { constructionTypeOptions, neighborhoods } from './constants';

//Component Begin

export default function PropertyDetails() {
  const params = useParams<string>();
  const [uploadError, setUploadError] = useState<string>('');
  const { upload } = useFileUpload({
    signAsset: signApplicationPublicAsset,
  });

  const { setCurrentStep, updateData, data: formContextData } = useApplicationFormContext();

  const formMethods = useFormContext<InferType<(typeof RentalRegistryValidationSchema)[1]>>();

  const {
    trigger,
    getValues,
    formState: { isValid },
    watch,
  } = formMethods;

  const familyType = watch('propertyDetails.familyType');

  const uploadFile = useMutation((payload: { signAssetPayload: ISignApplicationPublicAssetPayload[]; files: File[] }) => upload(payload), {
    onSuccess: ({ sign, files }: UploadFnReturn) => {
      let signRes: SignedApplicationAssetResponse[] = [];
      if (Array.isArray(sign)) {
        signRes = [...sign];
      } else {
        signRes = [{ ...sign }];
      }

      updateData({
        propertyDetails: {
          ...getValues('propertyDetails'),
          additionalDocs: signRes.map((sig, index) => ({
            name: files[index].name,
            path: sig.path,
            key: sig.key,
            type: files[index].type,
            field: 'additionalDocs',
            description: 'Additional Docs',
          })),
        },
      });
      setCurrentStep((prev) => prev + 1);
    },
    onError: (_error: any) => {
      setUploadError('Failed to upload document');
    },
  });

  // useEffect(() => {
  //   if (!isObjectEmpty(errors)) {
  //     const firstErrorKey = Object.keys(errors?.propertyDetails)[0];
  //     setFocus(`propertyDetails.${firstErrorKey}`);
  //   }
  // }, [errors, setFocus]);

  const onSubmit = () => {
    trigger();
    if (!isValid) {
      return;
    }

    const formData = getValues('propertyDetails');

    if (formData.additionalDocs?.length) {
      const formDataSelectedFiles = formData?.additionalDocs.map((f: File) => f.name);
      const formContextDataUploadedFiles = formContextData?.propertyDetails?.additionalDocs.map((f: File) => f.name);

      const fileUploadCompare = compareTwoArrays(formDataSelectedFiles, formContextDataUploadedFiles);

      if (fileUploadCompare.array1Diff.length > 0) {
        const signAssetPayload = formData.additionalDocs
          .filter((f: File) => fileUploadCompare.array1Diff.includes(f.name))
          .map((file: File) => ({
            fileName: file.name,
            fileType: file.type,
            fileSize: file.size,
            formId: String(params.applicationFormId),
          }));
        uploadFile.mutate({
          signAssetPayload,
          files: formData.additionalDocs,
        });
      } else {
        setCurrentStep((prev) => prev + 1);
      }
    } else {
      updateData({
        propertyDetails: { ...formData },
      });
      setCurrentStep((prev) => prev + 1);
    }
  };

  return (
    <div className='p-6 space-y-6 sm:rounded-r-md sm:overflow-hidden'>
      <div>
        <h3 className='text-lg leading-6 font-medium text-primaryColor-normal'>Property Details</h3>
        <p className='mt-1 text-sm text-gray-500'>Details about your property</p>
      </div>
      <div className='grid grid-cols-6 gap-6'>
        <HookInput
          formMethods={formMethods}
          customClassName='col-span-6 sm:col-span-6 lg:col-span-2'
          label='Street Address'
          name='propertyDetails.streetAddress'
          type='text'
        />
        <HookInput
          formMethods={formMethods}
          customClassName='col-span-6 sm:col-span-6 lg:col-span-2'
          label='City'
          name='propertyDetails.city'
          type='text'
          disabled
        />
        <HookSelect
          formMethods={formMethods}
          customClassName='col-span-6 sm:col-span-3 lg:col-span-2'
          label='State / Province'
          name='propertyDetails.state'
          options={States.map((s) => ({
            label: s.name,
            value: s.name,
          }))}
          disabled
        />
        <HookMaskedInput
          formMethods={formMethods}
          customClassName='col-span-6 sm:col-span-3 lg:col-span-2'
          label='Zip / Postal Code'
          name='propertyDetails.zipCode'
          maskedComponent='zipCode'
        />
        <HookSelect
          formMethods={formMethods}
          customClassName='col-span-6 sm:col-span-3 lg:col-span-2'
          label='Neighborhood'
          name='propertyDetails.neighborhood'
          options={neighborhoods.map((n) => ({ label: n, value: n }))}
        />
        <HookSelect
          customClassName='col-span-6 lg:col-span-2'
          label='Proposed Construction Type'
          name='propertyDetails.constructionType'
          options={constructionTypeOptions.map((c) => ({
            label: c,
            value: c,
          }))}
        />
        <HookRadioGroup
          formMethods={formMethods}
          className='col-span-6 md:col-span-6'
          label='Is this property less than 15 years old?'
          name='propertyDetails.isPropertyLessThan15YearsOld'
          id='is-property-city-own'
          options={[
            { label: 'Yes', value: 'yes' },
            { label: 'No', value: 'no' },
          ]}
        />
        <HookRadioGroup
          formMethods={formMethods}
          className='col-span-6 md:col-span-6'
          label='Is this a Single-family or Multi-Family project?'
          name='propertyDetails.familyType'
          id='family-type'
          options={[
            { label: 'Single Family', value: 'single' },
            { label: 'Multi-Family', value: 'multi' },
            { label: 'Not Applicable', value: 'not applicable' },
          ]}
        />
        {familyType === 'single' && (
          <div className='grid grid-cols-3 col-span-3 gap-3'>
            <label className='col-span-3'>What will be the average sales price?</label>
            <HookMaskedInput
              formMethods={formMethods}
              customClassName='col-span-1 sm:col-span-1 lg:col-span-1'
              name='propertyDetails.avgSalesPrice'
              id='avg-sales-price'
              maskedComponent='currency'
            />
          </div>
        )}
        {familyType === 'multi' && (
          <>
            <label className='col-span-6 leading-7 block text-sm font-medium text-neutralColor-medium'>
              Please provide the rental for the different unit types
            </label>
            <HookMaskedInput
              customClassName='col-span-6 lg:col-span-1'
              label='Studios'
              name='propertyDetails.rentType.studios'
              maskedComponent='currency'
            />
            <HookMaskedInput
              customClassName='col-span-6 lg:col-span-1'
              label='1 Bedroom'
              name='propertyDetails.rentType.oneBedroom'
              maskedComponent='currency'
            />
            <HookMaskedInput
              customClassName='col-span-6 lg:col-span-1'
              label='2 Bedrooms'
              name='propertyDetails.rentType.twoBedrooms'
              maskedComponent='currency'
            />
            <HookMaskedInput
              customClassName='col-span-6 lg:col-span-1'
              label='3 Bedrooms'
              name='propertyDetails.rentType.threeBedrooms'
              maskedComponent='currency'
            />
          </>
        )}
        <div className='col-span-6'>
          <HookTextarea
            formMethods={formMethods}
            name='propertyDetails.moreInfo'
            label='More Information About Project'
            placeholder='Please tell us more about this proposed project. Specifically highlight any timing sensitivity.'
            rows={5}
          />
        </div>
        <div className='col-span-6'>
          <HookFileInput
            formMethods={formMethods}
            name='propertyDetails.additionalDocs'
            label='Additional Docs'
            id='additionalDocs'
            placeholder='Upload Additional Documents'
            multiple
          />
        </div>
        {uploadError && <p className='text-tertiaryColor-normal text-md col-span-6'>{uploadError}</p>}
      </div>
      <div className='flex gap-4 justify-end mt-5'>
        <Button
          label='Previous'
          disabled={uploadFile.isLoading}
          btnStyle='btn-neutral'
          type='button'
          onClick={() => setCurrentStep((prev) => prev - 1)}
        />
        <Button
          label={uploadFile.isLoading ? 'Uploading Files...' : 'Next'}
          btnStyle={uploadFile?.isLoading ? 'neutral' : 'primary'}
          disabled={uploadFile.isLoading}
          type='button'
          onClick={onSubmit}
        />
      </div>
    </div>
  );
}
